/**
 * P4 사업자 스코어카드 (DESIGN §5.4 · RUNBOOK P7-1).
 *
 * 이 페이지가 지켜야 할 세 가지:
 *
 * 1. **"전국 몇 등"을 말하지 않는다.** 동급 매칭은 같은 지역·충전기 타입·연식
 *    조건을 통제한 뒤의 백분위다. 고속충전기만 운영하는 곳과 완속만 운영하는
 *    곳을 같은 자로 재면 그건 운영이 아니라 설비 구성을 재는 것이다 (§5.4).
 *    이 구분은 각주가 아니라 **본문 문장**으로 화면에 있어야 한다.
 * 2. **판정 보류를 숫자보다 먼저 말한다.** 실측에서 사업자 전원이 관측 창 미달로
 *    `insufficient_window` 다 (§3.7.1). 스코어카드가 백분위를 찍어 주기 때문에
 *    경고를 아래에 두면 순위표로 읽히고 만다 — 그래서 페이지 맨 위다.
 * 3. **백분위 하나를 크게 쓰지 않는다.** 동급 수가 적으면 백분위가 요동친다.
 *    크게 쓰는 것은 동급 수(n)이고, 백분위는 n 과 함께 문장으로 쓴다.
 */

import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { Section } from '@/components/Section'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { pendingNote } from '@/lib/window'
import { int, pct } from '@/lib/fmt'
import { useEntityLabel } from '@/lib/labels'
import { VERDICT_LABEL, type Envelope, type OperatorQuality, type Verdict } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import { OperatorPicker } from './scorecard/OperatorPicker'
import { PeerBenchmark, type ScorecardData } from './scorecard/PeerBenchmark'
import './ScorecardPage.css'

interface MatrixData {
  operators: OperatorQuality[]
  n_comparable: number
  n_total: number
}

const VERDICT_CLASS: Record<Verdict, string> = {
  comparable: 'scard__badge--ok',
  excluded: 'scard__badge--no',
  insufficient_window: 'scard__badge--pending',
}

/**
 * 비교가능 화이트리스트의 문턱 (§3.7).
 *
 * **판정의 권위는 백엔드에 있다.** 아래 문자열은 "이 값이 무엇과 견주는
 * 값인가"를 읽는 사람에게 알려주는 안내이고, 통과/미달의 결론은 응답의
 * `reasons` 가 말한다. 그래서 프론트에서 통과 여부를 다시 계산하지 않는다 —
 * 두 곳에서 판정하면 언젠가 화면이 백엔드와 다른 말을 한다.
 */
const CRITERION = {
  chargers: { text: '30기 이상', mark: null },
  na: { text: '20% 미만', mark: 0.2 },
  stale: { text: '20% 미만', mark: 0.2 },
  upddt: { text: '1% 미만', mark: 0.01 },
  codes: { text: '3종 이상 · 충전대기(2) 포함', mark: null },
  quality: { text: '0~1 가중합 (§8.4)', mark: null },
  window: { text: '28일 이상', mark: null },
} as const

export function ScorecardPage() {
  // 필터 바의 '사업자' 필터(다중, 집계 대상)와 다른 것이므로 전역 상태에 섞지
  // 않는다. 여기는 "누구의 스코어카드를 볼까"(단일)다
  const [busiId, setBusiId] = useState<string | null>(null)
  const filters = useFilters()
  const labelOf = useEntityLabel()

  const matrixQ = useQuery({
    queryKey: ['quality/operator_matrix', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<MatrixData>('/quality/operator_matrix', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const matrix = matrixQ.data as Envelope<MatrixData> | undefined
  const ops = matrix?.data.operators ?? []
  const nTotal = matrix?.data.n_total ?? ops.length
  const nComparable = matrix?.data.n_comparable ?? 0
  const nPending = ops.filter((o) => o.verdict === 'insufficient_window').length
  // 관측 창은 사업자별이 아니라 수집 전체의 성질이라 첫 행에서 읽어도 된다
  const windowDays = ops[0]?.window_days ?? null
  const op = busiId ? (ops.find((o) => o.busi_id === busiId) ?? null) : null

  return (
    <div className="scard">
      <header className="scard__intro">
        <p className="scard__lead">
          이 페이지는 사업자 한 곳을 <strong>동급과 견주는</strong> 화면입니다. 보여주는 것은
          &ldquo;전국 몇 등&rdquo;이 아니라 <strong>같은 지역·충전기 타입·연식(±2년) 조건을
          공유하는 동급 사업자 중 몇 백분위</strong>인지입니다 — 고속충전기만 운영하는 곳과
          완속만 운영하는 곳을 같은 자로 재면 그것은 운영이 아니라 설비 구성을 재는 일이
          됩니다.
        </p>

        {/* 판정 보류 경고 — 백분위보다 위에 둔다. 아래에 두면 순위표로 읽힌다 */}
        {ops.length > 0 && (
          <p className="scard__warn" role="alert">
            <strong>이 페이지의 숫자를 순위로 읽지 마세요.</strong> 사업자{' '}
            <span className="num">{int(nTotal)}</span>곳 중 비교가능 판정을 받은 곳은{' '}
            <span className="num">{int(nComparable)}</span>곳이고,{' '}
            <span className="num">{int(nPending)}</span>곳이 <strong>판정 보류</strong>입니다
            {windowDays != null && (
              <>
                {' '}
                — 관측 창 <span className="num">{windowDays.toFixed(1)}일</span>이 기준{' '}
                <span className="num">28일</span>에 못 미칩니다
              </>
            )}
            . 아래 백분위는 지금까지 관측된 것 안에서의 위치일 뿐, 사업자의 실력 순위가
            아닙니다.
          </p>
        )}

        <div className="scard__pick">
          <OperatorPicker value={busiId} onChange={setBusiId} />
          <span className="scard__pickhint">
            아래 두 카드가 함께 <strong>{busiId ? labelOf('busi', busiId) : '선택한 사업자'}</strong>{' '}
            기준으로 바뀝니다
          </span>
        </div>
      </header>

      {busiId == null ? (
        <p className="scard__wait" role="status">
          사업자 목록을 불러오는 중입니다…
        </p>
      ) : (
        <>
          <Section
            index={1}
            title="이 사업자의 데이터는 어떤 상태인가"
            lead="가동률을 읽기 전에 그 숫자가 무엇 위에 서 있는지 본다. 상태를 모르는 시간이 절반을 넘으면 가동률은 관측된 절반의 이야기다."
            layout="full"
          >
            <ProfileCard busiId={busiId} op={op} />
          </Section>

          <Section
            index={2}
            title="동급 안에서 어디쯤인가"
            lead="전국 한 줄 세우기가 아니다. 같은 지역·충전기 타입·연식 조건을 공유하는 사업자끼리만 견주고, 신뢰구간이 겹치는 상대와는 앞뒤를 주장하지 않는다."
            layout="full"
          >
            <PeerBenchmark busiId={busiId} />
          </Section>
        </>
      )}
    </div>
  )
}

/**
 * 사업자 프로파일 — 본문이 곧 표다 (§8.6.6 이므로 표 뷰 전환 버튼을 두지 않는다).
 *
 * **오염 지표 세 줄에만 막대를 그린다.** NA율·STALE율·updDt 오류율은 모두
 * "낮을수록 좋은" 비율이라 0~100% 한 눈금 위에 얹을 수 있다. 충전기 수·코드
 * 종류·DQ 점수는 단위가 다르거나 방향이 반대(높을수록 좋음)라 같은 축에 얹으면
 * 축이 거짓말을 한다 — 그래서 숫자만 쓴다.
 */
function ProfileCard({ busiId, op }: { busiId: string; op: OperatorQuality | null }) {
  const filters = useFilters()
  const labelOf = useEntityLabel()

  // 동급 벤치마크와 **같은 쿼리 키** — 같은 응답을 두 번 받아 오지 않는다
  const q = useQuery({
    queryKey: ['scorecard', busiId, filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<ScorecardData>(
        `/scorecard/${encodeURIComponent(busiId)}`,
        toQueryFilters(filters),
        {},
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<ScorecardData> | undefined
  const p = env?.data.profile ?? null
  const name = p?.busi_nm ?? labelOf('busi', busiId)
  const verdict = op?.verdict ?? null

  /** 지표 · 값 · 기준. `rate` 가 있는 줄만 0~100% 공통 눈금 위에 막대를 그린다 */
  const rows: {
    label: string
    value: ReactNode
    rate: number | null
    criterion: { text: string; mark: number | null }
  }[] = p
    ? [
        {
          label: '충전기',
          value: `${int(p.n_chargers)}기`,
          rate: null,
          criterion: CRITERION.chargers,
        },
        {
          label: 'NA율 (상태 모름)',
          value: pct(p.na_rate, 1),
          rate: p.na_rate,
          criterion: CRITERION.na,
        },
        {
          label: 'STALE율 (연계 끊김)',
          value: pct(p.stale_rate, 1),
          rate: p.stale_rate,
          criterion: CRITERION.stale,
        },
        {
          label: 'updDt 오류율',
          value: pct(p.upddt_error_rate, 2),
          rate: p.upddt_error_rate,
          criterion: CRITERION.upddt,
        },
        {
          label: '관측된 stat 코드',
          // ⚠ 만 찍으면 화면을 못 보는 사람에게는 아무 말도 하지 않는 표시가 된다.
          // 품질 매트릭스(§8.4)와 같은 문법으로 제목과 대체 문구를 함께 붙인다
          value: (
            <>
              {p.distinct_codes}종
              {op && !op.uses_code_2 && (
                <span className="scard__flag" title="'충전대기(2)' 코드를 한 번도 쓰지 않았습니다">
                  {' '}
                  ⚠<span className="sr-only"> 충전대기(2) 코드 미사용</span>
                </span>
              )}
            </>
          ),
          rate: null,
          criterion: CRITERION.codes,
        },
        {
          label: 'DQ 점수',
          value: p.quality.toFixed(3),
          rate: null,
          criterion: CRITERION.quality,
        },
        {
          label: '관측 창',
          value: op?.window_days != null ? `${op.window_days.toFixed(1)}일` : '—',
          rate: null,
          criterion: CRITERION.window,
        },
      ]
    : []

  return (
    <ChartFrame
      title={`${name} 프로파일`}
      question="이 사업자가 내놓는 데이터를 어디까지 믿을 수 있나"
      meta={env?.meta}
      headline={
        verdict ? (
          <Headline
            value={VERDICT_LABEL[verdict]}
            unit="비교가능 판정"
            tone={verdict === 'comparable' ? 'up' : 'warn'}
          />
        ) : undefined
      }
      reading="이 사업자가 API 로 내보낸 데이터의 상태를 지표별로 나열한 표입니다. 가동률 자체가 아니라 그 가동률이 무엇 위에 서 있는지를 봅니다."
      encoding={[
        {
          role: '가로축',
          what: '오염 지표 3종이 공유하는 0~100% 눈금',
          detail:
            'NA율·STALE율·updDt 오류율은 모두 "낮을수록 좋은" 비율이라 같은 눈금 위에 막대로 겹쳐 두었습니다. 50%와 0.7%가 몇 배 차이인지는 숫자보다 막대 길이가 먼저 말합니다. 막대를 가로지르는 세로 눈금이 비교가능 문턱(20% · 20% · 1%)이며, 기준 없는 막대는 "길어 보인다"밖에 말하지 못합니다. 충전기 수·코드 종류·DQ 점수에는 막대를 그리지 않았는데, 단위가 다르거나 방향이 반대(높을수록 좋음)인 값을 같은 축에 얹으면 축이 거짓말을 하기 때문입니다.',
        },
        {
          role: '값',
          what: '집계 창 기준 시간 가중 측정값',
          detail:
            '창을 자르지 않으면 수년 전 statUpdDt 꼬리가 분모를 지배해 모든 사업자가 실패로 나옵니다 (§3.7.1). 그래서 비율은 전 기간이 아니라 집계 창 안에서 잽니다.',
        },
        {
          role: '기준',
          what: '비교가능 화이트리스트 문턱 (§3.7)',
          detail:
            '읽는 사람을 위한 안내이며 판정 자체가 아닙니다. 통과/미달의 결론은 아래 사유 문장이 말합니다 — 프론트에서 다시 계산하지 않습니다. 두 곳에서 판정하면 언젠가 화면이 백엔드와 다른 말을 합니다.',
        },
        {
          role: '판정',
          what: '비교가능 · 제외 · 판정 보류',
          detail:
            '세 값은 서로 다른 진술입니다. 제외는 "기준에 미달한다"이고, 판정 보류는 "관측 창이 짧아 아직 판단할 수 없다"입니다.',
        },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && p == null}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '커버리지는 1 − NA율이다.',
          text: '이 카드 오른쪽 위의 커버리지 막대는 "이 사업자의 시간 중 상태를 알고 있던 비율"입니다. 가동률의 분모가 얼마나 두꺼운지를 뜻하며, 낮으면 아래 모든 숫자가 얇은 근거 위에 서 있다는 신호입니다.',
        },
        {
          label: "'판정 보류'는 '제외'가 아니다.",
          text: `제외는 기준 미달이고, 판정 보류는 관측 창이 28일 기준에 못 미쳐 아직 판단할 수 없다는 뜻입니다. ${pendingNote(op?.window_days ?? null)}`,
        },
        {
          label: '⚠ 는 코드 2 미사용 표시다.',
          text: "'관측된 stat 코드' 옆의 ⚠ 는 이 사업자가 '충전대기(2)' 코드를 한 번도 쓰지 않았다는 뜻입니다. 코드 종류가 많아도 이 코드가 없으면 '충전 중'과 '대기 중'을 가를 수 없어 비교가능 기준을 통과하지 못합니다.",
        },
        {
          label: '막대는 세 줄에만 있다.',
          text: '방향이 같은 비율(낮을수록 좋음)끼리만 한 눈금을 공유합니다. DQ 점수는 높을수록 좋으므로 같은 축에 얹지 않았습니다.',
        },
        {
          label: '이 표는 가동률에 대한 것이 아니다.',
          text: '"이 사업자가 상태를 얼마나 성실하게 보고하는가"를 재는 표입니다. 가동률과 그 위치는 아래 동급 벤치마크 카드에 있습니다.',
        },
      ]}
    >
      {p && (
        <>
          <div className="ctable">
            <table className="data-table">
              <caption className="sr-only">{name} 의 데이터 품질 지표와 비교가능 기준</caption>
              <thead>
                <tr>
                  <th scope="col">지표</th>
                  <th scope="col" className="ta-r">
                    값
                  </th>
                  <th scope="col">0~100% 눈금</th>
                  <th scope="col">기준</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label}>
                    <th scope="row">{r.label}</th>
                    <td className="num ta-r">{r.value}</td>
                    <td>
                      {r.rate != null ? (
                        <span className="scard__meter" aria-hidden="true">
                          <span
                            className="scard__meterfill"
                            style={{ width: `${Math.min(100, r.rate * 100)}%` }}
                          />
                          {/* 문턱을 눈금 위에 세워 둔다 — 기준 없는 막대는
                              "길어 보인다/짧아 보인다" 밖에 말하지 못한다 */}
                          {r.criterion.mark != null && (
                            <span
                              className="scard__metermark"
                              style={{ left: `${r.criterion.mark * 100}%` }}
                            />
                          )}
                        </span>
                      ) : (
                        <span className="scard__nometer">—</span>
                      )}
                    </td>
                    <td className="scard__criterion">{r.criterion.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="scard__verdictline">
            {verdict && (
              <span className={`scard__badge ${VERDICT_CLASS[verdict]}`}>
                {VERDICT_LABEL[verdict]}
              </span>
            )}
            <span className="scard__reason">
              {p.reasons ?? '판정 사유가 기록되지 않았습니다.'}
            </span>
          </p>
        </>
      )}
    </ChartFrame>
  )
}
